<script setup lang="ts">import { computed as _computed } from 'vue';

import { computed, ref } from "vue";
import { SwitchRoot, SwitchThumb } from "reka-ui";

import { useBrandStore } from "@/stores/brand-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { isEqual, getMarkStatus, compare, $can } from "@/services/UtilitiesService";

import { RsLoading } from "@/components";
import CaseEvidenceItem from "./CaseEvidenceItem.vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { Evidence } from "@/models/Evidence";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const routeParams = useCaseRouteParams();

const showForAllFiles = ref(true);

const viewerConfig = _computed(() => {
  return useBrandStore().viewerConfig;
});

const config = _computed(() => {
  return viewerConfig.value.evidenceThemes;
});

const filesByRef = _computed(() => {
  return useMetadataStore().filesByRef;
});

const themesToShow = _computed(() => {
  if (!useBrandStore().viewerConfig || !config.value?.enableFilters || !routeParams.value.casefile) {
    return "all";
  }

  const classification = filesByRef.value[routeParams.value.casefile]?.aiClassification || "unknown";

  if (classification.toLowerCase() === "unknown" || config.value.enableForUnknownClassifications) {
    const fc = config.value.filters?.find((e) => e.classification === classification);
    const hiddenThemes = useBrandStore().viewerConfig.evidenceThemes?.hiddenThemes ?? {};

    // classification not in filters, then all themes can be used
    if (fc == null) return "all";
    // Classification exists with a single null or empty array, no themes can be used
    if (fc.themes.includes(null) || fc.themes.length < 1) return "none";
    // Classification has themes, only themes in that list can be used
    return fc.themes.flatMap((theme) => (theme == null || hiddenThemes[theme] ? [] : theme.toLowerCase()));
  }

  return "none";
});

const candidates = _computed(() => {
  return useAnnotationStore()
    .annotations.flatMap<Evidence>((a) => {
      if (/user/i.test(a.source) || !/candidate/i.test(getMarkStatus(a))) {
        return [];
      }

      const file = filesByRef.value[a.fileReference];
      return (
        a.evidenceThemes?.flatMap((t) => {
          if (!/bookmark/i.test(t.usage) || !isEqual(t.status, "candidate")) return [];

          return {
            status: t.status,
            fileReference: a.fileReference,
            theme: t.evidenceTheme,
            type: "annotation",
            value: a.markId,
            displayName: file?.edxFilename,
          };
        }) ?? []
      );
    })
    .sort((a, b) => compare(a.value, b.value));
});

const hasFilterRestrictedItems = _computed(() => {
  return items.value.length > 0 && filteredItems.value.length === 0;
});

const filteredItems = computed(() => {
  const hiddenThemes = useBrandStore().viewerConfig.evidenceThemes?.hiddenThemes ?? {};

  const files = new Set(useMetadataStore().filteredFilesInCurrentFolder.map((f) => f.fileReference));

  return items.value.flatMap(({ name, entries }) => {
    if (hiddenThemes[name]) return [];

    return {
      name,
      entries: entries.filter(
        (e) => files.has(e.fileReference) && (showForAllFiles.value || e.fileReference === routeParams.value.casefile),
      ),
    };
  });
});

const canBookmarkFile = computed(() => {
  if (!$can("bookmark")) return false;

  const currentFile = useCaseFileStore().currentFile;

  if (currentFile == null || viewerConfig.value.features?.evidence === "disabled") return false;

  // Audio type processing
  if (["audio", "ac5"].includes(currentFile.fileType)) {
    if (viewerConfig.value.features?.transcript === "disabled") return false;

    const { formOnlyRecording, fileTranscript, fileReferences } = useCaseFileStore();

    // Form only recording?
    if (currentFile.fileType === "ac5" && formOnlyRecording) return false;

    if (!fileReferences?.voice) {
      console.warn("No voice file reference was found.");
      return false;
    }

    return fileTranscript?.status === "success";
  }

  return true;
});

const items = computed(() => {
  const candidatesByTheme = candidates.value.reduce(
    (acc, candidate) => {
      acc[candidate.theme] ??= [];
      acc[candidate.theme].push(candidate);
      return acc;
    },
    {} as Record<string, typeof candidates>,
  );

  return useEvidenceStore()
    .categoriesWithEvidences.filter((c) => c.usageTypes.has("bookmark"))
    .map(({ name, entries }) => ({
      name,
      entries: [...entries.filter((e) => /user/i.test(e.source ?? "")), ...(candidatesByTheme[name] ?? [])],
    }));
});

const loading = _computed(() => {
  return useEvidenceStore().loading;
});
</script>
<template>
  <div class="evidences tw-size-full tw-overflow-y-auto">
    <RsLoading v-if="loading" id="evidence-loading" message="Loading Evidence List" />
    <div v-else>
      <div class="tw-bg-gray-800 tw-p-1">
        <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-p-1 focus-within:tw-ring">
          <label for="evidenceFilteredRecords" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">
            All Filtered Records
          </label>
          <SwitchRoot
            id="evidenceFilteredRecords"
            v-model="showForAllFiles"
            :disabled="!routeParams.casefile"
            class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
          >
            <SwitchThumb
              class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
            />
          </SwitchRoot>
        </div>
      </div>
      <template v-if="filteredItems.length === 0">
        <h4 v-if="hasFilterRestrictedItems" class="tw-pt-3 tw-text-center tw-text-base tw-tracking-wide">
          There are no entries valid for this classification
        </h4>
        <h4 v-else class="tw-pt-3 tw-text-center tw-text-base tw-tracking-wide">There are no entries available</h4>
      </template>
      <template v-else>
        <table class="tw-table tw-h-full tw-w-full">
          <tbody>
            <CaseEvidenceItem
              v-for="(category, index) in filteredItems"
              :key="`category-${index}`"
              :category="category"
              :time="routeParams.time"
              :brand="routeParams.brand"
              :themes-to-show="themesToShow"
              :snippet="routeParams.snippet"
              :casename="routeParams.casename"
              :casefile="routeParams.casefile"
              :filepage="routeParams.filepage"
              :annotation="routeParams.annotation"
              :can-bookmark-file="canBookmarkFile"
              :filtered-categories="filteredItems"
            />
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.evidences {
  table {
    background-color: #2b1b4f;
    color: #fff;
    td {
      border-color: #915796;
    }
  }
}
</style>
